import * as React from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

import type { DuprProfileDto, DuprSsoConfigDto } from "@volley/data";

import fetchApi, { logFetchError } from "../../util/fetchApi";
import DuprConnect from "../Account/DuprConnect";

import useDialog from "./useDialog";

/**
 * Default prompt copy — the new-user onboarding framing from the design's
 * "Connect your DUPR® account" dialog (scenario 2, screen 2).
 */
const DEFAULT_DESCRIPTION =
    "Receive a rating if you don't have one, track your improvement, and train with recommendations matched to your level.";

interface InitialDuprConnectDialogProps {
    /**
     * Prompt copy shown above the connect button. Defaults to the onboarding
     * copy; pass a context-specific string/node when reusing this dialog in a
     * different flow.
     */
    description?: React.ReactNode;
}

/**
 * First-run DUPR connect step, chained after the new-user sport selector when
 * the player picks pickleball. Linking is offered but skippable here (unlike the
 * assessment flow, where it's required) — skipping just closes onboarding and
 * Home shows "Connect DUPR". Reuses {@link DuprConnect} for the SSO flow (and its
 * prompt copy via `description`) and owns the "Skip for Now" affordance itself.
 */
export default function InitialDuprConnectDialog({
    description = DEFAULT_DESCRIPTION,
}: InitialDuprConnectDialogProps): React.JSX.Element {
    const { setDialogType } = useDialog();
    const [ssoConfig, setSsoConfig] = React.useState<DuprSsoConfigDto | null>(
        null,
    );
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let active = true;
        fetchApi<DuprSsoConfigDto>("/api/integrations/dupr/config")
            .then((cfg) => {
                if (active) setSsoConfig(cfg);
            })
            .catch((e) => logFetchError(e))
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    const close = React.useCallback(() => setDialogType(null), [setDialogType]);

    // A successful link surfaces a non-null profile — finish onboarding.
    const handleProfileChange = React.useCallback(
        (profile: DuprProfileDto | null) => {
            if (profile) close();
        },
        [close],
    );

    const duprEnabled = ssoConfig?.enabled === true;

    return (
        <>
            <DialogTitle sx={{ p: 2 }} color="primary.main">
                Connect your DUPR® account
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <CircularProgress size={28} />
                ) : duprEnabled && ssoConfig ? (
                    <DuprConnect
                        ssoConfig={ssoConfig}
                        description={description}
                        onProfileChange={handleProfileChange}
                    />
                ) : (
                    <Typography color="text.secondary">
                        DUPR connection isn&apos;t available right now. You can
                        link later from your profile.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>Skip for Now</Button>
            </DialogActions>
        </>
    );
}
